import React, { useState, useEffect, useCallback } from 'react';
import { Clock, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Activity types that the system actually logs today (for the filter dropdown).
const TYPES = ['user_login', 'user_registered', 'invoice_uploaded', 'ai_extraction_completed',
    'invoice_approved', 'invoice_rejected', 'invoice_paid_elsewhere', 'voucher_created', 'voucher_pushed',
    'voucher_deleted', 'document_deleted', 'document_exported', 'company_created', 'subscription_activated',
    'staff_added', 'staff_access_changed', 'user_deleted'];

const LIMIT = 50;

export default function AdminActivityPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState('all');
    const [email, setEmail] = useState('');
    const [skip, setSkip] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: LIMIT, skip };
            if (type !== 'all') params.activity_type = type;
            const r = await adminAPI.activity(params);
            setData(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not load activity'); }
        finally { setLoading(false); }
    }, [type, skip]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setSkip(0); }, [type]);

    const rows = (data?.rows || []).filter((r) =>
        !email.trim() || (r.user_email || '').toLowerCase().includes(email.trim().toLowerCase()) || (r.user_name || '').toLowerCase().includes(email.trim().toLowerCase()));
    const total = data?.total || 0;

    return (
        <div className="space-y-6" data-testid="admin-activity">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <Clock className="h-7 w-7 text-primary" /> Activity & audit
                    </h1>
                    <p className="text-muted-foreground mt-1">Platform-wide activity log — who did what, when, for which firm/client. Read-only.</p>
                </div>
                <Button variant="ghost" size="icon" onClick={load} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="h-9 w-56"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value="all">All activity types</SelectItem>
                        {TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Input className="h-9 w-56" placeholder="Filter by user name/email (this page)" value={email} onChange={(e) => setEmail(e.target.value)} />
                <span className="text-sm text-muted-foreground">{total.toLocaleString('da-DK')} events</span>
            </div>

            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Events</CardTitle></CardHeader>
                <CardContent className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="activity-table">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                                <th className="py-2 pr-3">When</th><th className="py-2 pr-3">Who</th>
                                <th className="py-2 pr-3">Action</th><th className="py-2 pr-3">Firm</th>
                                <th className="py-2 pr-3">Client</th><th className="py-2 pr-3">Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No events.</td></tr>
                            ) : rows.map((r, i) => (
                                <tr key={i} className="border-b last:border-0">
                                    <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{r.timestamp ? new Date(r.timestamp).toLocaleString('da-DK') : '—'}</td>
                                    <td className="py-2 pr-3 max-w-[14rem] truncate" title={r.user_email || ''}>{r.user_name || '—'}</td>
                                    <td className="py-2 pr-3"><Badge variant="outline" className="whitespace-nowrap">{(r.activity_type || '').replace(/_/g, ' ')}</Badge></td>
                                    <td className="py-2 pr-3 max-w-[12rem] truncate">{r.firm || '—'}</td>
                                    <td className="py-2 pr-3 max-w-[12rem] truncate">{r.client || '—'}</td>
                                    <td className="py-2 pr-3 max-w-[16rem] truncate text-muted-foreground">{r.detail || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between mt-3 text-sm">
                        <span className="text-muted-foreground">Showing {skip + 1}–{Math.min(skip + LIMIT, total)} of {total.toLocaleString('da-DK')}</span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))}><ChevronLeft className="h-4 w-4" /> Prev</Button>
                            <Button variant="outline" size="sm" disabled={skip + LIMIT >= total} onClick={() => setSkip(skip + LIMIT)}>Next <ChevronRight className="h-4 w-4" /></Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
                Shows what the platform currently logs (login, uploads, AI extraction, approvals/rejections, vouchers, exports, deletions, company/user/staff/subscription changes). Not yet logged: bank-statement import, bank coding accept/correct, password-reset requests, and GDPR erasures (audited separately in gdpr_erasures). IP addresses are not captured.
            </p>
        </div>
    );
}
